import axios, { AxiosResponse } from "axios";
import { UserType } from "../types/user.type";
import { GetUserOfflineProfileResponse } from "../types/offline.type";
import { environment } from "../environment";

export class UserHelper {

  /**
   * Update the last login of the user
   *
   * @param user User object to update the last login
   * @returns {Promise<boolean>} True if the last login was updated successfully
   */
  static async updateUserLastLogin(
    user: UserType | undefined
  ): Promise<boolean> {
    if (!user) return false;

    const response: AxiosResponse = await axios.patch(
      `${environment.api.host}/api/user/${user.id}/last-login`,
      {},
      {
        headers: {
          Authorization: `Bearer ${user.access_token}`,
        },
      }
    );

    return response.status === 200 || response.status === 201;
  }

  /**
   * Update the user groups from the local storage
   */
  static async updateUserGroups(): Promise<UserType | undefined> {
    const userFromLocalStorage = localStorage.getItem("user");

    if (!userFromLocalStorage) return undefined;

    const userFromLocalStorageParsed = JSON.parse(userFromLocalStorage);

    try {
      const response: AxiosResponse = await axios.get(
        `${environment.api.host}/api/profile`,
        {
          headers: {
            Authorization: `Bearer ${userFromLocalStorageParsed.access_token}`,
          },
        }
      );

      const usersGroups = response.data.items[0].users_groups || [];

      userFromLocalStorageParsed.users_groups = usersGroups;
      return userFromLocalStorageParsed;
    } catch (error) {
      console.error("Error fetching user profile:", error);
      return undefined;
    }
  }

  /**
   * Get offline user profile from API
   * Use this to fetch complete offline profile data for storage in Electron app
   *
   * @param includeIcon - Si se deben incluir los iconos en base64 (pesados)
   * @returns Offline user profile from API or undefined if not found
   */
  static async getOfflineUserProfile(includeIcon: boolean = false): Promise<GetUserOfflineProfileResponse | undefined> {
    const userFromLocalStorage = localStorage.getItem("user");

    if (!userFromLocalStorage) return undefined;

    try {
      const userFromLocalStorageParsed = JSON.parse(userFromLocalStorage) as UserType;

      const url = `${environment.api.host}/api/offline/profile${includeIcon ? '?includeIcon=true' : ''}`;

      const response: AxiosResponse<GetUserOfflineProfileResponse> = await axios.get(
        url,
        {
          headers: {
            Authorization: `Bearer ${userFromLocalStorageParsed.access_token}`,
          },
        }
      );

      return response.data;

    } catch (error) {
      console.error("Error fetching offline user profile:", error);
      return undefined;
    }
  }
}
